import React, { useState } from 'react'
import { isUndefined } from 'lodash'
import { sortInstallationFiles } from '../functions'
import DocumentFile from './DocumentFile'
import { BlankView, BlankViewImageSizes, BlankViewImageVariants } from 'src/Components/blank-view'
import { useTypedSelector } from 'src/State/RootReducer'
import { useNavigate } from 'react-router'
import { FileWithLatestStatus } from 'src/Libs/installation-statement/models'

interface Props {
  files?: FileWithLatestStatus[]
  installationId: string | undefined
  isLoading?: boolean
  fileClassName?: string
  fileContentClassName?: string
}

/**
 * Component to display all files or placeholder, if no files are available.
 * Show nothing while files are loading.
 * @returns all available files
 */
const FileList: React.FC<Props> = ({ files, installationId, isLoading = false, fileClassName, fileContentClassName }) => {
  const [openedImages, setOpenedImages] = useState<number[]>([])
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const navigate = useNavigate()
  const redirectUrl = installationId && storageId ? `/dashboard/overview/${installationId}/${storageId}` : '/'

  if (!isLoading && ((files && files.length === 0) || isUndefined(files)))
    return (
      <BlankView
        imageVariant={BlankViewImageVariants.Neutral}
        imageSize={BlankViewImageSizes.Small}
        fullHeight
        title="documents.noFiles.info"
        explanation="documents.blank-view.subtitle"
        action={{
          text: 'common.back-to-dashboard',
          onClick: () => navigate(redirectUrl),
        }}
      />
    )

  /**
   * This function handles opened images list to disable opening multiple images on the same time
   */
  const handleOpenedImages = (id: number, opened: boolean): void => {
    if (opened) {
      setOpenedImages(images => [...images, id])
    } else {
      setOpenedImages(images => [...images.filter(imageId => imageId !== id)])
    }
  }

  const sortedFiles = sortInstallationFiles(files ?? [])

  if (sortedFiles.length === 0) return <></>

  return (
    <>
      {sortedFiles.map(file => {
        return (
          <DocumentFile
            key={file.id}
            file={file}
            installationId={installationId}
            disableOpenImage={openedImages.length > 0}
            setImageOpened={(fileId, value) => handleOpenedImages(fileId, value)}
            fileClassName={fileClassName}
            fileContentClassName={fileContentClassName}
          />
        )
      })}
    </>
  )
}

export default FileList
